<template>
  <div class="ml-1 mr-1">
    <fb-drop-area
      @handle-drop="insertComponent(0, true, 0, true, 0, $event)"
      :alwaysShow="false"
      :formId="formId"
    />
    <div
      class="d-flex justify-content-center align-items-center w-100 color-custom-grey"
      :style="{ minHeight: height ? height : 'calc(100vh - 290px)' }"
      v-if="json.rows.length == 0"
    >
      Add form components
    </div>
    <div :key="row.id" v-for="(row, rowIndex) in json.rows">
      <component
        :is="getElementComponentData('Row', row.version)"
        :properties="row.properties"
        :id="row.id"
        :formId="formId"
        showType="form-builder"
        @delete-row="deleteRow(rowIndex)"
      >
        <fb-drop-area
          @handle-drop="insertComponent(rowIndex, false, 0, true, 0, $event)"
          :alwaysShow="false"
          :formId="formId"
        />
        <div
          v-frag
          :key="column.id"
          v-for="(column, columnIndex) in row.columns"
        >
          <component
            :is="getElementComponentData('Column', column.version)"
            :properties="column.properties"
            :id="column.id"
            :formId="formId"
            showType="form-builder"
          >
            <fb-form-builder
              :json="column.layout"
              :formId="formId"
              v-if="column.layout"
              @update-json="
                newJson => updateLayout(rowIndex, columnIndex, newJson)
              "
            >
            </fb-form-builder>
            <div class="components text-left" v-if="column.components">
              <div
                v-frag
                :key="component.id"
                v-for="(component, componentIndex) in column.components"
              >
                <drag
                  :effect-allowed="['move']"
                  drop-effect="move"
                  class="drag"
                  :transfer-data="component"
                  @dragstart="
                    (transferData, nativeEvent) =>
                      beginMovingComponent(
                        component.id,
                        transferData,
                        nativeEvent
                      )
                  "
                  @dragend="endMovingComponent()"
                  :hideImageHtml="false"
                >
                  <template slot="image">
                    <div
                      class="component-container px-2 py-1"
                      :class="
                        selectedElementControlId == component.id
                          ? 'bg-extra-light-gray component-container-selected'
                          : ''
                      "
                    >
                      <component
                        :is="
                          getElementComponentData(
                            component.elType,
                            column.version
                          )
                        "
                        :properties="component.properties"
                        :id="component.id"
                        :formId="formId"
                        showType="form-builder"
                        @update-properties="
                          newProperties =>
                            updateProperties(
                              rowIndex,
                              columnIndex,
                              componentIndex,
                              newProperties
                            )
                        "
                      />
                      <div class="actions-component rounded d-flex">
                        <div class="action" v-if="isTechStaff || !hasComputedProperties(component.properties)">
                          <img
                            width="18"
                            height="18"
                            src="/icons/edit.svg"
                            class="b-action-item"
                            @click="
                              setFormBuilderEditingElementEdit(component.id)
                            "
                          />
                        </div>
                        <div class="action">
                          <img
                            width="18"
                            height="18"
                            src="/icons/delete.svg"
                            class="b-action-item"
                            @click="
                              deleteComponent(
                                rowIndex,
                                columnIndex,
                                componentIndex
                              )
                            "
                          />
                        </div>
                      </div>
                      <div class="id-component">
                        {{ component.id }}
                      </div>
                      <div
                        v-if="
                          !getElementComponentConfig(component.elType)
                            .builderEnabled
                        "
                        class="disabled-component"
                      ></div>
                    </div>
                  </template>
                </drag>
              </div>
            </div>
          </component>
          <fb-drop-area
            @handle-drop="
              insertComponent(rowIndex, false, columnIndex + 1, true, 0, $event)
            "
            :alwaysShow="false"
            :formId="formId"
          />
        </div>
      </component>
      <fb-drop-area
        @handle-drop="insertComponent(rowIndex + 1, true, 0, true, 0, $event)"
        :alwaysShow="false"
        :formId="formId"
      />
    </div>
  </div>
</template>

<script>
import {
  getElementComponentData,
  getElementComponentLastVersion,
  getElementComponents,
  getElementComponentConfig
} from "../../helpers/formBuilder";
import { mapMutations, mapState, mapGetters } from "vuex";

export default {
  name: "FormBuilderArea",
  props: ["json", "formId", "height"],
  methods: {
    ...mapMutations("formBuilder", [
      "setFormBuilderSelectedElementControlId",
      "setFormBuilderMovingComponent",
      "setFormBuilderEditingElement",
      "setFormBuilderDragging"
    ]),
    getElementComponentData(elementType, elementVersion) {
      return getElementComponentData(elementType, elementVersion).componentName;
    },
    getElementComponentConfig(elementType) {
      return getElementComponentConfig(elementType);
    },
    getElementComponents() {
      return getElementComponents();
    },
    addLayout(rowIndex, columnIndex) {
      const newJson = { ...this.json };
      newJson.rows[rowIndex].columns[columnIndex].layout = {
        rows: []
      };
      if (newJson.rows[rowIndex].columns[columnIndex].components)
        delete newJson.rows[rowIndex].columns[columnIndex].components;
      this.$emit("update-json", newJson);
    },
    updateLayout(rowIndex, columnIndex, newLayoutJson) {
      const newJson = { ...this.json };
      newJson.rows[rowIndex].columns[columnIndex].layout = newLayoutJson;
      this.$emit("update-json", newJson);
    },
    updateProperties(rowIndex, columnIndex, componentIndex, newProperties) {
      const newJson = { ...this.json };
      newJson.rows[rowIndex].columns[columnIndex].components[
        componentIndex
      ].properties = newProperties;
      this.$emit("update-json", newJson);
    },
    insertComponent(
      rowIndex,
      isNewRow,
      columnIndex,
      isNewColumn,
      componentIndex,
      componentJson
    ) {
      const newJson = { ...this.json };
      if (isNewRow) {
        const rowVersion = getElementComponentLastVersion("Row");
        const rowData = getElementComponentData("Row", rowVersion);
        const newRow = {
          elType: "Row",
          version: getElementComponentLastVersion("Row"),
          id: this.getElementUniqueId({
            elementType: "Row",
            formId: this.formId
          }),
          ...rowData.defaultAttrs,
          properties: {},
          columns: []
        };
        newJson.rows.splice(rowIndex, 0, newRow);
      }
      if (isNewColumn) {
        const columnVersion = getElementComponentLastVersion("Column");
        const columnData = getElementComponentData("Column", columnVersion);
        const newColumn = {
          elType: "Column",
          version: getElementComponentLastVersion("Column"),
          id: this.getElementUniqueId({
            elementType: "Column",
            formId: this.formId
          }),
          ...columnData.defaultAttrs,
          properties: {
            //size: 12 / columnCount
          }
        };
        newJson.rows[rowIndex].columns.splice(columnIndex, 0, newColumn);
      }
      if (!newJson.rows[rowIndex].columns[columnIndex].components)
        newJson.rows[rowIndex].columns[columnIndex].components = [];

      newJson.rows[rowIndex].columns[columnIndex].components.splice(
        componentIndex,
        0,
        JSON.parse(JSON.stringify(componentJson))
      );
      if (newJson.rows[rowIndex].columns[columnIndex].layout)
        delete newJson.rows[rowIndex].columns[columnIndex].layout;
      this.$emit("update-json", newJson);
    },
    deleteComponent(rowIndex, columnIndex, componentIndex) {
      this.$dialog
        .confirm(
          {
            title: "Are you sure you want to delete this component?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(() => {
          const newJson = { ...this.json };
          newJson.rows[rowIndex].columns[columnIndex].components.splice(
            componentIndex,
            1
          );
          this.setSelectedElement(null);
          this.setFormBuilderEditingElement({
            editing: false,
            formId: this.formId
          });
          this.$emit("update-json", newJson);
        })
        .catch(function() {});
    },
    setSelectedElement(controlId) {
      this.setFormBuilderSelectedElementControlId({
        selectedElementControlId: controlId,
        formId: this.formId
      });
    },
    beginMovingComponent(componentId, transferData, nativeEvent) {
      nativeEvent.stopPropagation();
      this.setFormBuilderMovingComponent({
        movingComponentId: componentId,
        formId: this.formId
      });
      this.setFormBuilderDragging({ dragging: true, formId: this.formId });
    },
    endMovingComponent() {
      this.setFormBuilderMovingComponent({
        movingComponentId: null,
        formId: this.formId
      });
      this.setFormBuilderDragging({ dragging: false, formId: this.formId });
    },
    setFormBuilderEditingElementEdit(controlId) {
      this.setSelectedElement(controlId);
      this.setFormBuilderEditingElement({ editing: true, formId: this.formId });
    },
    hasComputedProperties(properties){
      const values = Object.values(properties);
      const match = values.find(element => {
        if (typeof element === 'string' && element.includes("{{") && !element.match(/\{\{collections.(?:(?!\}\}).)*\}\}/g)) {
          return true;
        }
      });
      return match !== undefined;
    }
  },
  computed: {
    ...mapState("formBuilder", {
      forms: state => state.form
    }),
    selectedElementControlId() {
      return this.forms[this.formId].selectedElementControlId;
    },
    ...mapGetters("formBuilder", ["getElementUniqueId"]),
    ...mapGetters("layout", ["isTechStaff"]),
  }
};
</script>

<style>
.component-container {
  border: 1px solid transparent;
  position: relative;
}
.component-container-selected {
  background-color: #f5f8fa !important;
  box-shadow: 2px 2px 3px 1px rgba(0, 0, 0, 0.25);
}
.component-container:hover {
  background-color: #f5f8fa !important;
  cursor: move;
}
.actions-component {
  position: absolute;
  top: 2px;
  right: 13px;
  display: none !important;
  z-index: 5;
}
.id-component {
  position: absolute;
  bottom: -2px;
  right: 13px;
  padding: 3px;
  display: none !important;
  font-size: 0.7rem;
  z-index: 1;
}
.action {
  width: 25px;
  text-align: center;
}
.action:hover {
  cursor: pointer;
}
.layout {
  margin: 5px 5px;
}
.dragger {
  position: absolute;
  left: -17px;
  top: -1px;
  bottom: -1px;
  display: flex;
  flex: 1;
  align-items: center;
  background-color: blue;
  color: white;
  display: none;
  z-index: 5;
}
.component-container:hover > .actions-component,
.component-container:hover > .id-component,
.component-container:hover > .dragger,
.component-container-selected > .actions-component,
.component-container-selected > .id-component,
.component-container-selected > .dragger {
  display: flex !important;
}
.component-container:hover > .drag > .dragger {
  display: flex !important;
}
.component-container > .drag {
  overflow: hidden;
}
.drag-image {
  position: fixed;
  top: -5000px;
}
.disabled-component {
  position: absolute;
  top: 25px;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}
</style>
